import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Building2, Calendar, Plus, Shield, ChevronDown, Trash2, Edit2, ArrowLeft, Save, X, FileText, Database, Download, Upload } from 'lucide-react';
import api from '../services/api';
import { User, Team, Meeting } from '../types';

interface AdminDashboardProps {
    currentUser: User;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ currentUser }) => {
    const [activeTab, setActiveTab] = useState<'users' | 'teams' | 'meetings' | 'submissions' | 'backup'>('users');
    const [users, setUsers] = useState<User[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [meetings, setMeetings] = useState<Meeting[]>([]);

    // Submissions State
    const [moodEntries, setMoodEntries] = useState<any[]>([]);
    const [feedbacks, setFeedbacks] = useState<any[]>([]);
    const [filters, setFilters] = useState({
        userId: '',
        teamId: '',
        meetingId: ''
    });

    // Backup State
    const [restoring, setRestoring] = useState(false);
    const [restoreFile, setRestoreFile] = useState<File | null>(null);
    const [isSystemRestoring, setIsSystemRestoring] = useState(false);
    const [systemRestoreStatus, setSystemRestoreStatus] = useState("");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Create User State
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '', color_preference: '#000000' });

    // Create Team State
    const [showCreateTeam, setShowCreateTeam] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');

    // Create/Edit Meeting State
    const [showCreateMeeting, setShowCreateMeeting] = useState(false);
    const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
    const [meetingForm, setMeetingForm] = useState({ name: '', team_id: '', frequency: '', status: 'planned', description: '' });

    // Manage Members State
    const [showManageMembers, setShowManageMembers] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [teamMembers, setTeamMembers] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState('');

    // Manage Sessions State
    const [showManageSessions, setShowManageSessions] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
    const [meetingSessions, setMeetingSessions] = useState<any[]>([]);
    const [newSessionDate, setNewSessionDate] = useState('');

    // Manage Attendees State
    const [showManageAttendees, setShowManageAttendees] = useState(false);
    const [meetingAttendees, setMeetingAttendees] = useState<User[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Determine what to fetch based on active tab to optimize, or fetch all needed for dropdowns
                const promises: Promise<any>[] = [
                    api.get('/admin/users'),
                    api.get('/admin/teams'),
                    api.get('/admin/meetings')
                ];

                if (activeTab === 'submissions') {
                    const params = {
                        user_id: filters.userId || undefined,
                        team_id: filters.teamId || undefined,
                        meeting_id: filters.meetingId || undefined
                    };
                    promises.push(api.get('/admin/submissions/mood', { params }));
                    promises.push(api.get('/admin/submissions/feedback', { params }));
                }

                const results = await Promise.all(promises);

                setUsers(results[0].data);
                setTeams(results[1].data);
                setMeetings(results[2].data);

                if (activeTab === 'submissions') {
                    setMoodEntries(results[3].data);
                    setFeedbacks(results[4].data);
                }
            } catch (err: any) {
                setError(err.response?.data?.detail || 'Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        fetchData();
    }, [activeTab, filters]);

    // System WebSocket
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        // Construct WS URL - use /ws directly as per Ingress rule (not /api/v1/ws)
        const wsUrl = `${protocol}//${window.location.host}/ws/system/notifications?token=${token}`;

        const ws = new WebSocket(wsUrl);

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'RESTORE_STARTED') {
                    setIsSystemRestoring(true);
                    setSystemRestoreStatus("System restore in progress... Please wait.");
                } else if (data.type === 'RESTORE_COMPLETED') {
                    setSystemRestoreStatus("Restore successful! Reloading...");
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                } else if (data.type === 'RESTORE_FAILED') {
                    setIsSystemRestoring(false);
                    setError(`Restore Failed: ${data.error || 'Unknown error'}`);
                }
            } catch (e) {
                // Ignore non-json messages
            }
        };

        return () => {
            ws.close();
        };
    }, []);

    // --- User Actions ---
    const handleRoleChange = async (userId: number, newRole: string) => {
        try {
            await api.put(`/admin/users/${userId}/role`, { role: newRole });
            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole as User['role'] } : u));
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to update role');
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.email || !newUser.password) return;
        try {
            const res = await api.post('/admin/users', newUser);
            setUsers([...users, res.data]);
            setNewUser({ email: '', password: '', full_name: '', color_preference: '#000000' });
            setShowCreateUser(false);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to create user');
        }
    };

    const handleDeleteUser = async (userId: number) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await api.delete(`/admin/users/${userId}`);
            setUsers(users.filter(u => u.id !== userId));
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to delete user');
        }
    };

    // --- Team Actions ---
    const handleCreateTeam = async () => {
        if (!newTeamName.trim()) return;
        try {
            const res = await api.post('/admin/teams', { name: newTeamName });
            setTeams([...teams, res.data]);
            setNewTeamName('');
            setShowCreateTeam(false);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to create team');
        }
    };

    const handleDeleteTeam = async (teamId: number) => {
        if (!window.confirm('Are you sure you want to delete this team?')) return;
        try {
            await api.delete(`/admin/teams/${teamId}`);
            setTeams(teams.filter(t => t.id !== teamId));
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to delete team');
        }
    };

    const openManageMembers = async (team: Team) => {
        setSelectedTeam(team);
        setTeamMembers([]);
        setShowManageMembers(true);
        try {
            const res = await api.get(`/teams/${team.id}/members`);
            setTeamMembers(res.data);
        } catch (err: any) {
            setError('Failed to fetch team members');
        }
    };

    const handleAddMember = async () => {
        if (!selectedTeam || !selectedUserId) return;
        try {
            const user = users.find(u => u.id === parseInt(selectedUserId));
            if (!user) return;

            await api.post(`/teams/${selectedTeam.id}/members`, {
                team_id: selectedTeam.id,
                user_email: user.email
            });

            setTeamMembers([...teamMembers, user]);
            setSelectedUserId('');
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to add member');
        }
    };

    const handleRemoveMember = async (userId: number) => {
        if (!selectedTeam) return;
        try {
            await api.delete(`/teams/${selectedTeam.id}/members/${userId}`);
            setTeamMembers(teamMembers.filter(m => m.id !== userId));
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to remove member');
        }
    };

    // --- Meeting Actions ---
    const handleCreateMeeting = async () => {
        if (!meetingForm.name || !meetingForm.team_id) {
            setError("Name and Team are required");
            return;
        }
        try {
            const res = await api.post('/admin/meetings', { ...meetingForm, team_id: parseInt(meetingForm.team_id) });
            setMeetings([...meetings, res.data]);
            setMeetingForm({ name: '', team_id: '', frequency: '', status: 'planned', description: '' });
            setShowCreateMeeting(false);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to create meeting');
        }
    };

    const handleUpdateMeeting = async () => {
        if (!editingMeeting || !meetingForm.name) return;
        try {
            const updateData: any = { ...meetingForm };
            if (meetingForm.team_id) updateData.team_id = parseInt(meetingForm.team_id);

            const res = await api.put(`/admin/meetings/${editingMeeting.id}`, updateData);
            setMeetings(meetings.map(m => m.id === editingMeeting.id ? res.data : m));
            setEditingMeeting(null);
            setMeetingForm({ name: '', team_id: '', frequency: '', status: 'planned', description: '' });
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to update meeting');
        }
    };

    const handleDeleteMeeting = async (meetingId: number) => {
        if (!window.confirm('Are you sure you want to delete this meeting?')) return;
        try {
            await api.delete(`/admin/meetings/${meetingId}`);
            setMeetings(meetings.filter(m => m.id !== meetingId));
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to delete meeting');
        }
    };

    const startEditMeeting = (meeting: Meeting) => {
        setEditingMeeting(meeting);
        setMeetingForm({
            name: meeting.name,
            team_id: meeting.team_id.toString(),
            frequency: meeting.frequency || '',
            status: meeting.status,
            description: meeting.description || ''
        });
    };

    const openManageSessions = async (meeting: Meeting) => {
        setSelectedMeeting(meeting);
        setMeetingSessions([]);
        setShowManageSessions(true);
        try {
            const res = await api.get(`/meetings/${meeting.id}/sessions`);
            setMeetingSessions(res.data);
        } catch (err: any) {
            setError('Failed to fetch sessions');
        }
    };

    const handleCreateSession = async () => {
        if (!selectedMeeting || !newSessionDate) return;
        try {
            const res = await api.post(`/sessions/`, {
                meeting_id: selectedMeeting.id,
                date: new Date(newSessionDate).toISOString(),
                status: 'active'
            });
            setMeetingSessions([res.data, ...meetingSessions]);
            setNewSessionDate('');
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to create session');
        }
    };

    const handleDeleteSession = async (sessionId: number) => {
        if (!window.confirm('Delete this session?')) return;
        try {
            await api.delete(`/sessions/${sessionId}`);
            setMeetingSessions(meetingSessions.filter(s => s.id !== sessionId));
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to delete session');
        }
    };

    // --- Attendees Actions ---
    const openManageAttendees = async (meeting: Meeting) => {
        setSelectedMeeting(meeting);
        setMeetingAttendees([]);
        setShowManageAttendees(true);
        try {
            const res = await api.get(`/admin/meetings/${meeting.id}/attendees`);
            setMeetingAttendees(res.data);
        } catch (err: any) {
            setError('Failed to fetch attendees');
        }
    };

    const handleAddAttendee = async () => {
        if (!selectedMeeting || !selectedUserId) return;
        try {
            const user = users.find(u => u.id === parseInt(selectedUserId));
            if (!user) return;

            await api.post(`/admin/meetings/${selectedMeeting.id}/attendees`, {
                meeting_id: selectedMeeting.id,
                user_email: user.email
            });

            setMeetingAttendees([...meetingAttendees, user]);
            setSelectedUserId('');
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to add attendee');
        }
    };

    const handleRemoveAttendee = async (userId: number) => {
        if (!selectedMeeting) return;
        try {
            await api.delete(`/admin/meetings/${selectedMeeting.id}/attendees/${userId}`);
            setMeetingAttendees(meetingAttendees.filter(u => u.id !== userId));
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : JSON.stringify(detail)) || 'Failed to remove attendee');
        }
    };

    // --- Submission Actions ---
    const handleDeleteMoodEntry = async (id: number) => {
        if (!window.confirm('Delete this mood entry?')) return;
        try {
            await api.delete(`/admin/submissions/mood/${id}`);
            setMoodEntries(moodEntries.filter(e => e.id !== id));
        } catch (err: any) {
            setError('Failed to delete mood entry');
        }
    };

    const handleDeleteFeedback = async (id: number) => {
        if (!window.confirm('Delete this feedback?')) return;
        try {
            await api.delete(`/admin/submissions/feedback/${id}`);
            setFeedbacks(feedbacks.filter(f => f.id !== id));
        } catch (err: any) {
            setError('Failed to delete feedback');
        }
    };

    const tabs = [
        { key: 'users', label: 'Users', icon: Users },
        { key: 'teams', label: 'Teams', icon: Building2 },
        { key: 'meetings', label: 'Meetings', icon: Calendar },
        { key: 'submissions', label: 'Submissions', icon: FileText },
        { key: 'backup', label: 'Backup / Restore', icon: Database },
    ] as const;

    const handleDownloadBackup = async () => {
        try {
            const response = await api.get('/admin/backup', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `moodboard_backup_${new Date().toISOString()}.sql`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setError('Failed to download backup');
        }
    };

    const handleRestoreBackup = async () => {
        if (!restoreFile) return;
        if (!window.confirm('WARNING: This will overwrite the entire database. Are you sure?')) return;

        setRestoring(true);
        const formData = new FormData();
        formData.append('file', restoreFile);

        try {
            await api.post('/admin/restore', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            // Do not reload manually - wait for WebSocket 'RESTORE_COMPLETED' event
            // alert('Database restored successfully. Please refresh the page.');
            // window.location.reload();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to restore database');
        } finally {
            setRestoring(false);
            setRestoreFile(null);
        }
    };

    const roleOptions = ['admin', 'team_head', 'user'];
    const roleColors: Record<string, string> = {
        admin: 'bg-purple-100 text-purple-800',
        team_head: 'bg-blue-100 text-blue-800',
        user: 'bg-gray-100 text-gray-800',
    };

    return (
        <div className="w-full max-w-6xl mx-auto p-6">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8 text-purple-600" />
                    <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                </div>
                <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    Back to MoodBoard
                </Link>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg relative flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
                </div>
            )}

            {/* System Restore Modal */}
            {isSystemRestoring && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
                        <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Restoring Database</h2>
                        <p className="text-gray-600 mb-4">{systemRestoreStatus}</p>
                        <div className="p-4 bg-yellow-50 text-yellow-800 text-sm rounded-lg">
                            <p className="font-semibold">⚠️ Do not close this window</p>
                            <p>The system is performing a full reset. This may take a minute.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-4 border-b border-gray-200 mb-8">
                {tabs.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`flex items-center gap-2 pb-4 px-2 text-lg font-medium transition-colors ${activeTab === key
                            ? 'border-b-2 border-purple-600 text-purple-600'
                            : 'text-gray-500 hover:text-gray-800'
                            }`}
                    >
                        <Icon className="w-5 h-5" />
                        {label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 min-h-[400px]">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Users Tab */}
                        {activeTab === 'users' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-bold">All Users ({users.length})</h3>
                                    <button
                                        onClick={() => setShowCreateUser(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        New User
                                    </button>
                                </div>

                                {showCreateUser && (
                                    <div className="p-4 bg-gray-50 rounded-lg mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input type="text" placeholder="Full Name" className="px-4 py-2 border rounded-lg" value={newUser.full_name} onChange={e => setNewUser({ ...newUser, full_name: e.target.value })} />
                                        <input type="email" placeholder="Email" className="px-4 py-2 border rounded-lg" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                                        <input type="password" placeholder="Password" className="px-4 py-2 border rounded-lg" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                                        <input type="color" className="h-10 w-full" value={newUser.color_preference} onChange={e => setNewUser({ ...newUser, color_preference: e.target.value })} />
                                        <div className="md:col-span-2 flex justify-end gap-2">
                                            <button onClick={() => setShowCreateUser(false)} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
                                            <button onClick={handleCreateUser} className="px-4 py-2 bg-purple-600 text-white rounded-lg">Create User</button>
                                        </div>
                                    </div>
                                )}

                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Email</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Role</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {users.map(user => (
                                            <tr key={user.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div
                                                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                                            style={{ backgroundColor: user.color_preference || '#6366f1' }}
                                                        >
                                                            {user.full_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                                                        </div>
                                                        <span className="font-medium">{user.full_name || 'Unnamed'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-gray-600">{user.email}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[user.role]}`}>
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative inline-block">
                                                            <select
                                                                value={user.role}
                                                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                                disabled={user.id === currentUser.id}
                                                                className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {roleOptions.map(role => (
                                                                    <option key={role} value={role}>{role}</option>
                                                                ))}
                                                            </select>
                                                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                                        </div>
                                                        {user.id !== currentUser.id && (
                                                            <button
                                                                onClick={() => handleDeleteUser(user.id)}
                                                                className="text-gray-400 hover:text-red-600 transition-colors"
                                                                title="Delete User"
                                                            >
                                                                <Trash2 className="w-5 h-5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Teams Tab */}
                        {activeTab === 'teams' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-bold">All Teams ({teams.length})</h3>
                                    <button
                                        onClick={() => setShowCreateTeam(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        New Team
                                    </button>
                                </div>

                                {showCreateTeam && (
                                    <div className="p-4 bg-gray-50 rounded-lg mb-4 flex gap-4">
                                        <input
                                            type="text"
                                            placeholder="Team name"
                                            value={newTeamName}
                                            onChange={(e) => setNewTeamName(e.target.value)}
                                            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                        <button
                                            onClick={handleCreateTeam}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                        >
                                            Create
                                        </button>
                                        <button
                                            onClick={() => setShowCreateTeam(false)}
                                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {teams.map(team => (
                                        <div key={team.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h4 className="font-bold text-lg">{team.name}</h4>
                                                    <p className="text-sm text-gray-500">ID: {team.id}</p>
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteTeam(team.id)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => openManageMembers(team)}
                                                className="w-full px-4 py-2 bg-purple-50 text-purple-700 text-sm font-semibold rounded-lg hover:bg-purple-100 transition-colors"
                                            >
                                                Manage Members
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Manage Members Modal */}
                                {showManageMembers && selectedTeam && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                                <h3 className="text-xl font-bold">Members: {selectedTeam.name}</h3>
                                                <button onClick={() => setShowManageMembers(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                                    <X className="w-5 h-5 text-gray-500" />
                                                </button>
                                            </div>
                                            <div className="p-6 space-y-4">
                                                <div className="flex gap-2">
                                                    <select
                                                        className="flex-1 px-4 py-2 border rounded-lg"
                                                        value={selectedUserId}
                                                        onChange={(e) => setSelectedUserId(e.target.value)}
                                                    >
                                                        <option value="">Select User to Add...</option>
                                                        {users.filter(u => !teamMembers.some(m => m.id === u.id)).map(u => (
                                                            <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        onClick={handleAddMember}
                                                        disabled={!selectedUserId}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                        Add
                                                    </button>
                                                </div>

                                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                                    {teamMembers.map(member => (
                                                        <div key={member.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-xs">
                                                                    {member.full_name?.charAt(0) || member.email.charAt(0).toUpperCase()}
                                                                </div>
                                                                <span>{member.full_name || member.email}</span>
                                                            </div>
                                                            <button
                                                                onClick={() => handleRemoveMember(member.id)}
                                                                className="text-gray-400 hover:text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {teamMembers.length === 0 && (
                                                        <p className="text-center text-gray-400 py-4">No members yet.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Meetings Tab */}
                        {activeTab === 'meetings' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-bold">All Meetings ({meetings.length})</h3>
                                    <button
                                        onClick={() => {
                                            setEditingMeeting(null);
                                            setMeetingForm({ name: '', team_id: '', frequency: '', status: 'planned', description: '' });
                                            setShowCreateMeeting(true);
                                        }}
                                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        New Meeting
                                    </button>
                                </div>

                                {(showCreateMeeting || editingMeeting) && (
                                    <div className="p-4 bg-gray-50 rounded-lg mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input type="text" placeholder="Meeting Name" className="px-4 py-2 border rounded-lg" value={meetingForm.name} onChange={e => setMeetingForm({ ...meetingForm, name: e.target.value })} />
                                        <select className="px-4 py-2 border rounded-lg bg-white" value={meetingForm.team_id} onChange={e => setMeetingForm({ ...meetingForm, team_id: e.target.value })}>
                                            <option value="">Select Team</option>
                                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                        <input type="text" placeholder="Frequency (e.g. Weekly)" className="px-4 py-2 border rounded-lg" value={meetingForm.frequency} onChange={e => setMeetingForm({ ...meetingForm, frequency: e.target.value })} />
                                        <select className="px-4 py-2 border rounded-lg bg-white" value={meetingForm.status} onChange={e => setMeetingForm({ ...meetingForm, status: e.target.value })}>
                                            <option value="planned">Planned</option>
                                            <option value="active">Active</option>
                                            <option value="archived">Archived</option>
                                        </select>
                                        <textarea placeholder="Description" className="md:col-span-2 px-4 py-2 border rounded-lg" value={meetingForm.description} onChange={e => setMeetingForm({ ...meetingForm, description: e.target.value })} />
                                        <div className="md:col-span-2 flex justify-end gap-2">
                                            <button onClick={() => { setShowCreateMeeting(false); setEditingMeeting(null); }} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
                                            <button onClick={editingMeeting ? handleUpdateMeeting : handleCreateMeeting} className="px-4 py-2 bg-purple-600 text-white rounded-lg">
                                                {editingMeeting ? 'Update Meeting' : 'Create Meeting'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Team</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Frequency</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {meetings.map(meeting => (
                                            <tr key={meeting.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 font-medium">{meeting.name}</td>
                                                <td className="px-4 py-3 text-gray-600">{teams.find(t => t.id === meeting.team_id)?.name || meeting.team_id}</td>
                                                <td className="px-4 py-3 text-gray-600">{meeting.frequency || '-'}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${meeting.status === 'active' ? 'bg-green-100 text-green-800' :
                                                        meeting.status === 'planned' ? 'bg-yellow-100 text-yellow-800' :
                                                            meeting.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                                                                'bg-orange-100 text-orange-800'
                                                        }`}>
                                                        {meeting.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={() => openManageSessions(meeting)}
                                                            className="px-3 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded hover:bg-purple-100 transition-colors"
                                                        >
                                                            Sessions
                                                        </button>
                                                        <button
                                                            onClick={() => openManageAttendees(meeting)}
                                                            className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded hover:bg-blue-100 transition-colors"
                                                        >
                                                            Attendees
                                                        </button>
                                                        <button
                                                            onClick={() => startEditMeeting(meeting)}
                                                            className="text-gray-400 hover:text-purple-600 transition-colors"
                                                            title="Edit"
                                                        >
                                                            <Edit2 className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteMeeting(meeting.id)}
                                                            className="text-gray-400 hover:text-red-600 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Manage Sessions Modal */}
                                {showManageSessions && selectedMeeting && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                                <h3 className="text-xl font-bold">Sessions: {selectedMeeting.name}</h3>
                                                <button onClick={() => setShowManageSessions(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                                    <X className="w-5 h-5 text-gray-500" />
                                                </button>
                                            </div>
                                            <div className="p-6 space-y-4">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="date"
                                                        className="flex-1 px-4 py-2 border rounded-lg"
                                                        value={newSessionDate}
                                                        onChange={(e) => setNewSessionDate(e.target.value)}
                                                    />
                                                    <button
                                                        onClick={handleCreateSession}
                                                        disabled={!newSessionDate}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                        Add Session
                                                    </button>
                                                </div>

                                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                                    {meetingSessions.map(session => (
                                                        <div key={session.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex items-center gap-3">
                                                                <span className="font-mono text-sm">{new Date(session.date).toLocaleDateString()}</span>
                                                                <span className={`text-xs px-2 py-0.5 rounded-full ${session.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                                                                    {session.status}
                                                                </span>
                                                            </div>
                                                            <button
                                                                onClick={() => handleDeleteSession(session.id)}
                                                                className="text-gray-400 hover:text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {meetingSessions.length === 0 && (
                                                        <p className="text-center text-gray-400 py-4">No sessions yet.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Manage Attendees Modal */}
                                {showManageAttendees && selectedMeeting && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                                <h3 className="text-xl font-bold">Attendees: {selectedMeeting.name}</h3>
                                                <button onClick={() => setShowManageAttendees(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                                    <X className="w-5 h-5 text-gray-500" />
                                                </button>
                                            </div>
                                            <div className="p-6 space-y-4">
                                                <p className="text-sm text-gray-500">
                                                    Limit visibility to specific users. If empty, all team members can access.
                                                </p>
                                                <div className="flex gap-2">
                                                    <select
                                                        className="flex-1 px-4 py-2 border rounded-lg"
                                                        value={selectedUserId}
                                                        onChange={(e) => setSelectedUserId(e.target.value)}
                                                    >
                                                        <option value="">Select User to Add...</option>
                                                        {users.filter(u => !meetingAttendees.some(m => m.id === u.id)).map(u => (
                                                            <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        onClick={handleAddAttendee}
                                                        disabled={!selectedUserId}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                        Add
                                                    </button>
                                                </div>

                                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                                    {meetingAttendees.map(user => (
                                                        <div key={user.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-xs">
                                                                    {user.full_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                                                                </div>
                                                                <span>{user.full_name || user.email}</span>
                                                            </div>
                                                            <button
                                                                onClick={() => handleRemoveAttendee(user.id)}
                                                                className="text-gray-400 hover:text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {meetingAttendees.length === 0 && (
                                                        <p className="text-center text-gray-400 py-4">No specific attendees (Open to Team).</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'submissions' && (
                            <div className="space-y-8">
                                {/* Filters */}
                                <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap gap-4 items-end">
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Team</label>
                                        <select
                                            className="w-full px-3 py-2 border rounded-lg"
                                            value={filters.teamId}
                                            onChange={(e) => setFilters({ ...filters, teamId: e.target.value, meetingId: '' })}
                                        >
                                            <option value="">All Teams</option>
                                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Meeting</label>
                                        <select
                                            className="w-full px-3 py-2 border rounded-lg"
                                            value={filters.meetingId}
                                            onChange={(e) => setFilters({ ...filters, meetingId: e.target.value })}
                                            disabled={!filters.teamId && meetings.length > 50} // Optional optimization
                                        >
                                            <option value="">All Meetings</option>
                                            {meetings
                                                .filter(m => !filters.teamId || m.team_id.toString() === filters.teamId)
                                                .map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by User</label>
                                        <select
                                            className="w-full px-3 py-2 border rounded-lg"
                                            value={filters.userId}
                                            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                                        >
                                            <option value="">All Users</option>
                                            {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                                        </select>
                                    </div>
                                    {/* Reset Button */}
                                    <button
                                        onClick={() => setFilters({ userId: '', teamId: '', meetingId: '' })}
                                        className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                                    >
                                        Reset
                                    </button>
                                </div>

                                {/* Mood Entries Section */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-bold">Mood Entries ({moodEntries.length})</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">User</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Emotion</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Coordinates</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {moodEntries.map(entry => (
                                                    <tr key={entry.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-gray-600 font-mono text-sm">
                                                            {new Date(entry.created_at).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {entry.user?.full_name || entry.user_id}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                                                                {entry.emotion}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-500 text-sm">
                                                            ({entry.x_coord}, {entry.y_coord})
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <button
                                                                onClick={() => handleDeleteMoodEntry(entry.id)}
                                                                className="text-gray-400 hover:text-red-600 transition-colors"
                                                                title="Delete Entry"
                                                            >
                                                                <Trash2 className="w-5 h-5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {moodEntries.length === 0 && (
                                                    <tr><td colSpan={5} className="text-center py-4 text-gray-400">No mood entries found.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Feedback Section */}
                                <div className="space-y-4 pt-4 border-t border-gray-100">
                                    <h3 className="text-xl font-bold">Feedback Submissions</h3>
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">User</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Ratings</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Proposals</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {feedbacks.map(fb => (
                                                <tr key={fb.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-gray-600 font-mono text-sm">
                                                        {new Date(fb.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600">
                                                        {fb.user?.full_name || fb.user_id}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex gap-1">
                                                            {Object.entries(fb.ratings || {}).map(([k, v]) => (
                                                                <span key={k} className="px-1.5 py-0.5 bg-gray-100 rounded text-xs" title={k}>
                                                                    {k[0].toUpperCase()}:{v as number}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="text-xs space-y-1">
                                                            {fb.ssc_proposals?.map((p: any, i: number) => (
                                                                <div key={i} className="truncate max-w-[200px]">
                                                                    <span className={`font-bold ${p.type === 'start' ? 'text-green-600' : p.type === 'stop' ? 'text-red-600' : 'text-blue-600'}`}>
                                                                        {p.type.toUpperCase()}:
                                                                    </span> {p.content}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <button
                                                            onClick={() => handleDeleteFeedback(fb.id)}
                                                            className="text-gray-400 hover:text-red-600 transition-colors"
                                                            title="Delete Feedback"
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {feedbacks.length === 0 && (
                                                <tr><td colSpan={5} className="text-center py-4 text-gray-400">No feedback found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Backup Tab */}
                        {activeTab === 'backup' && (
                            <div className="space-y-8 p-4">
                                <div className="p-6 bg-blue-50 border border-blue-100 rounded-xl space-y-4">
                                    <h3 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                                        <Download className="w-6 h-6" />
                                        Backup Database
                                    </h3>
                                    <p className="text-blue-700">
                                        Create a full snapshot of the database (users, teams, meetings, history).
                                        The file can be used to restore the system later.
                                    </p>
                                    <button
                                        onClick={handleDownloadBackup}
                                        className="px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md transition-all active:scale-95"
                                    >
                                        Download SQL Backup
                                    </button>
                                </div>

                                <div className="p-6 bg-red-50 border border-red-100 rounded-xl space-y-4">
                                    <h3 className="text-xl font-bold text-red-900 flex items-center gap-2">
                                        <Upload className="w-6 h-6" />
                                        Restore Database
                                    </h3>
                                    <div className="p-4 bg-white rounded-lg border border-red-200">
                                        <p className="text-red-600 font-bold mb-2">⚠ DANGER ZONE</p>
                                        <p className="text-gray-600 text-sm mb-4">
                                            Restoring a backup will <strong>DELETE ALL CURRENT DATA</strong> and replace it with the backup contents.
                                            This action cannot be undone.
                                        </p>

                                        <div className="flex flex-col gap-4">
                                            <input
                                                type="file"
                                                accept=".sql"
                                                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                                                className="block w-full text-sm text-gray-500
                                                file:mr-4 file:py-2 file:px-4
                                                file:rounded-full file:border-0
                                                file:text-sm file:font-semibold
                                                file:bg-red-50 file:text-red-700
                                                hover:file:bg-red-100"
                                            />

                                            <button
                                                onClick={handleRestoreBackup}
                                                disabled={!restoreFile || restoring}
                                                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                {restoring ? 'Restoring...' : 'Restore Database Now'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
